'use client';

import { useQuery } from '@tanstack/react-query';
import { characterKeys, type CharacterScope } from '../queryKeys';
import { getCharacters } from '../actions/getCharacters';
import { getStoryCharacters, type StoryCharacterGroup } from '../actions/getStoryCharacters';
import type { Character } from '../types';

/**
 * Library/user characters (bookId NULL) by default. Pass scope='story' for
 * book-scoped auto chars, or 'all' for both. Backward compatible: existing
 * callers `useCharacters(familyId)` / `useCharacters(familyId, role)` keep the
 * library-only behavior.
 */
export function useCharacters(
  familyId: string | null | undefined,
  role?: string,
  scope: CharacterScope = 'library',
) {
  return useQuery<Character[]>({
    queryKey: characterKeys.list(familyId ?? '', role, scope),
    queryFn: () => getCharacters(role, scope),
    enabled: !!familyId,
  });
}

/**
 * Book-scoped ("from stories") characters grouped by their book. Powers the
 * "From stories" tab in the library page and wizard selector.
 */
export function useStoryCharacters(familyId: string | null | undefined) {
  return useQuery<StoryCharacterGroup[]>({
    queryKey: characterKeys.storyList(familyId ?? ''),
    queryFn: () => getStoryCharacters(),
    enabled: !!familyId,
  });
}
